// The app is worth installing (§20: phones are ~90% of staff usage), and the
// two platforms offer nothing in common: Android fires beforeinstallprompt and
// gives a real button, iOS fires nothing at all and needs Share → Add to Home
// Screen spelled out. Neither has anything to say once it IS installed.
export type InstallAdvice = 'hidden' | 'prompt' | 'ios-steps';

export type InstallWindow = {
  readonly standalone: boolean;
  readonly ios: boolean;
  readonly canPrompt: boolean;
};

// Standalone is how a launched PWA differs from a tab, on both platforms: the
// display-mode media query on Android, and a non-standard flag on iOS Safari.
export type StandaloneWindow = {
  readonly matchMedia?: (query: string) => { readonly matches: boolean };
  readonly navigator: object;
};

export function installedAlready(win: StandaloneWindow): boolean {
  // navigator.standalone is non-standard and iOS Safari's only tell, so the
  // cast is where the lie about Navigator's shape is kept.
  if ((win.navigator as { standalone?: unknown }).standalone === true) return true;
  return win.matchMedia?.('(display-mode: standalone)').matches === true;
}

// iOS Safari has no install event, so it is told apart by what it is. iPadOS
// reports itself as a Mac, which the touch points give away.
export function isIosBrowser(navigator: {
  userAgent: string;
  maxTouchPoints?: number;
  standalone?: boolean;
}): boolean {
  const agent = navigator.userAgent;
  if (/iPhone|iPod/.test(agent)) return true;
  if (/iPad/.test(agent)) return true;
  return /Macintosh/.test(agent) && (navigator.maxTouchPoints ?? 0) > 1;
}

export function installAdvice(win: InstallWindow): InstallAdvice {
  if (win.standalone) return 'hidden';
  if (win.canPrompt) return 'prompt';
  return win.ios ? 'ios-steps' : 'hidden';
}

const DISMISSED = 'kratispot.install-dismissed';

// Dismissed once, gone for good on that device: a banner that comes back every
// morning is an interruption, and §20 says the controls own a third of a phone
// at most. localStorage, not session: the answer outlives the tab.
export function dismissedInstall(storage: Pick<Storage, 'getItem'>): boolean {
  try {
    return storage.getItem(DISMISSED) === 'yes';
  } catch {
    return false;
  }
}

export function rememberDismissed(storage: Pick<Storage, 'setItem'>): void {
  try {
    storage.setItem(DISMISSED, 'yes');
  } catch {
    return;
  }
}
